import createStore from 'zustand'
import localForage from 'localforage'
import {useEffect} from 'react'

const settingsDb = localForage.createInstance({name: `plebonesSettings`})

const useSettingsStore = createStore((setState, getState) => ({
  settings: {},
  loadedSettings: new Set(),
  setSetting: async (settingName, settingValue) => {
    setState((state) => ({
      settings: {...state.settings, [settingName]: settingValue},
    }))
    // Fix: Store the settingValue, not settingName
    await settingsDb.setItem(settingName, settingValue)
  },
  loadSetting: async (settingName, defaultValue) => {
    const state = getState()
    if (state.loadedSettings.has(settingName)) {
      return state.settings[settingName]
    }

    try {
      const storedValue = await settingsDb.getItem(settingName)
      const value = storedValue !== null ? storedValue : defaultValue

      setState((state) => ({
        settings: {...state.settings, [settingName]: value},
        loadedSettings: new Set([...state.loadedSettings, settingName]),
      }))

      return value
    } catch (error) {
      console.error(`Error loading setting ${settingName}:`, error)
      setState((state) => ({
        settings: {...state.settings, [settingName]: defaultValue},
        loadedSettings: new Set([...state.loadedSettings, settingName]),
      }))
      return defaultValue
    }
  },
  resetSettings: async () => {
    setState({settings: {}, loadedSettings: new Set()})
    await settingsDb.clear()
  },
}))

const useSetting = (settingName, defaultValue = undefined) => {
  const settingValue = useSettingsStore((state) => state.settings[settingName])
  const setSetting = useSettingsStore((state) => state.setSetting)
  const loadSetting = useSettingsStore((state) => state.loadSetting)
  const isLoaded = useSettingsStore((state) => state.loadedSettings.has(settingName))

  // Load setting from storage on first use
  useEffect(() => {
    if (!isLoaded) {
      loadSetting(settingName, defaultValue)
    }
  }, [settingName, defaultValue, isLoaded, loadSetting])

  const setSettingValue = (settingValue) => setSetting(settingName, settingValue)

  // Return the stored value if loaded, otherwise return the default
  const currentValue = isLoaded ? settingValue : defaultValue

  return [currentValue, setSettingValue]
}

export default useSetting
